import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Badge, Button, Modal, Nav, ProgressBar } from 'react-bootstrap'

import ProviderService from '../../services/ProviderService'

import DuplicateClusterCard from './cards/DuplicateClusterCard'
import MalformedNameCard from './cards/MalformedNameCard'
import MisclassifiedCard from './cards/MisclassifiedCard'
import UnassignedClinicianCard from './cards/UnassignedClinicianCard'

export default function SmartLibraryReviewModal({ show, onHide, providerType, onComplete }) {
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [auditData, setAuditData] = useState(null)
  const [activeCategory, setActiveCategory] = useState('all')
  const [resolvedMap, setResolvedMap] = useState({}) // { [id]: boolean }
  const [orgOptions, setOrgOptions] = useState([])
  const [batchApplying, setBatchApplying] = useState(false)
  const modalBodyRef = useRef(null)

  const loadAudit = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const data = await ProviderService.getLibraryAudit(providerType)
      setAuditData(data)
      setResolvedMap({})
    } catch (err) {
      setError(err?.message || 'Failed to scan provider library.')
    } finally {
      setIsLoading(false)
    }
  }, [providerType])

  const loadOrgOptions = useCallback(async () => {
    try {
      const data = await ProviderService.search({
        searchText: '',
        classification: 'Organization',
        page: 1,
        take: 100,
        perPage: 100,
      })
      const results = (data?.results || []).map(p => {
        const orgName = (p.organization || p.organizationName || p.name || '').trim()
        const parts = [orgName, p.specialties, p.address].filter(Boolean)
        return {
          id: p.id,
          label: parts.join(' | '),
          value: orgName,
        }
      })
      setOrgOptions(results)
    } catch {
      // ignore
    }
  }, [])

  useEffect(() => {
    if (show) {
      loadAudit()
      loadOrgOptions()
    }
  }, [show, loadAudit, loadOrgOptions])

  const handleResolvedChange = (id, isResolved) => {
    setResolvedMap(prev => ({
      ...prev,
      [id]: isResolved,
    }))
  }

  const duplicates = auditData?.duplicateClusters || []
  const misclassified = auditData?.misclassifiedProviders || []
  const malformed = auditData?.malformedProviders || []
  const unassigned = auditData?.unassignedClinicians || []

  const totalIssues = duplicates.length + misclassified.length + malformed.length + unassigned.length

  const resolvedCount = useMemo(() => {
    return Object.values(resolvedMap).filter(Boolean).length
  }, [resolvedMap])

  const progressPercent = totalIssues > 0 ? Math.round((resolvedCount / totalIssues) * 100) : 0

  const handleBatchFixNames = async () => {
    setBatchApplying(true)
    try {
      const unresolvedMalformed = malformed.filter(m => !resolvedMap[m.providerId])
      await Promise.all(
        unresolvedMalformed.map(async m => {
          try {
            await ProviderService.fixMalformedName({
              providerId: m.providerId,
              title: m.suggestedTitle,
              firstName: m.suggestedFirstName,
              middleName: m.suggestedMiddleName,
              lastName: m.suggestedLastName,
              suffix: m.suggestedSuffix,
            })
            handleResolvedChange(m.providerId, true)
          } catch {
            // ignore individual batch failure
          }
        }),
      )
    } finally {
      setBatchApplying(false)
    }
  }

  const handleClose = () => {
    if (onComplete) onComplete()
    onHide()
  }

  return (
    <Modal
      show={show}
      onHide={handleClose}
      onEntered={() => modalBodyRef.current?.focus()}
      size="xl"
      backdrop="static"
      style={{ zIndex: 1060 }}
    >
      <Modal.Header closeButton className="bg-light">
        <div>
          <Modal.Title className="h5 mb-0">
            <span role="img" aria-label="sparkles" className="mr-2">
              ✨
            </span>
            AI Smart Library Review
          </Modal.Title>
          <div className="small text-muted mt-1">
            Automated hygiene review: deduplicate providers, resolve misclassifications, format names, and affiliate
            individuals with organizations.
          </div>
        </div>
      </Modal.Header>

      <Modal.Body ref={modalBodyRef} tabIndex={-1} className="pt-3">
        {error && <Alert variant="danger">{error}</Alert>}

        {isLoading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Scanning provider library...</span>
            </div>
            <div className="mt-3 text-muted">
              Scanning directory for duplicates, misclassifications, and malformed records...
            </div>
          </div>
        ) : totalIssues === 0 ? (
          <div className="text-center py-5">
            <div style={{ fontSize: '3rem' }}>🎉</div>
            <h5 className="mt-3 font-weight-bold text-dark">Your Provider Library is in Great Shape!</h5>
            <p className="text-muted">
              No duplicates, misclassifications, or malformed provider entries were detected across{' '}
              {auditData?.totalProvidersScanned || 0} records.
            </p>
            <Button variant="outline-primary" size="sm" onClick={handleClose}>
              Done
            </Button>
          </div>
        ) : (
          <>
            {/* Progress Bar & Stats */}
            <div className="mb-3 p-3 bg-light rounded border">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <span className="small font-weight-bold text-dark">
                  Resolution Progress: {resolvedCount} of {totalIssues} Issues Reviewed
                </span>
                <span className="small text-muted font-weight-bold">{progressPercent}%</span>
              </div>
              <ProgressBar
                now={progressPercent}
                variant={progressPercent === 100 ? 'success' : 'primary'}
                style={{ height: '8px' }}
              />
            </div>

            {/* Navigation Tabs */}
            <div className="d-flex justify-content-between align-items-center mb-3">
              <Nav variant="pills" activeKey={activeCategory} onSelect={k => setActiveCategory(k)}>
                <Nav.Item>
                  <Nav.Link eventKey="all" className="py-1 px-3">
                    All Issues{' '}
                    <Badge variant="secondary" className="ml-1">
                      {totalIssues}
                    </Badge>
                  </Nav.Link>
                </Nav.Item>
                {duplicates.length > 0 && (
                  <Nav.Item>
                    <Nav.Link eventKey="duplicates" className="py-1 px-3">
                      Duplicates{' '}
                      <Badge variant="warning" className="ml-1">
                        {duplicates.length}
                      </Badge>
                    </Nav.Link>
                  </Nav.Item>
                )}
                {misclassified.length > 0 && (
                  <Nav.Item>
                    <Nav.Link eventKey="misclassified" className="py-1 px-3">
                      Misclassified{' '}
                      <Badge variant="info" className="ml-1">
                        {misclassified.length}
                      </Badge>
                    </Nav.Link>
                  </Nav.Item>
                )}
                {malformed.length > 0 && (
                  <Nav.Item>
                    <Nav.Link eventKey="malformed" className="py-1 px-3">
                      Malformed Names{' '}
                      <Badge variant="secondary" className="ml-1">
                        {malformed.length}
                      </Badge>
                    </Nav.Link>
                  </Nav.Item>
                )}
                {unassigned.length > 0 && (
                  <Nav.Item>
                    <Nav.Link eventKey="unassigned" className="py-1 px-3">
                      Unaffiliated Individuals{' '}
                      <Badge variant="success" className="ml-1">
                        {unassigned.length}
                      </Badge>
                    </Nav.Link>
                  </Nav.Item>
                )}
              </Nav>

              {malformed.length > 0 && (
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={handleBatchFixNames}
                  disabled={batchApplying || malformed.every(m => resolvedMap[m.providerId])}
                >
                  {batchApplying ? 'Applying Corrections...' : '✨ Apply All Name Fixes'}
                </Button>
              )}
            </div>

            {/* Cards Deck */}
            <div style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: '4px' }}>
              {/* Duplicates */}
              {(activeCategory === 'all' || activeCategory === 'duplicates') &&
                duplicates.map(cluster => (
                  <DuplicateClusterCard
                    key={cluster.clusterId}
                    cluster={cluster}
                    onResolvedChange={handleResolvedChange}
                  />
                ))}

              {/* Misclassified */}
              {(activeCategory === 'all' || activeCategory === 'misclassified') &&
                misclassified.map(item => (
                  <MisclassifiedCard
                    key={item.providerId}
                    item={item}
                    orgOptions={orgOptions}
                    onResolvedChange={handleResolvedChange}
                  />
                ))}

              {/* Malformed */}
              {(activeCategory === 'all' || activeCategory === 'malformed') &&
                malformed.map(item => (
                  <MalformedNameCard key={item.providerId} item={item} onResolvedChange={handleResolvedChange} />
                ))}

              {/* Unassigned */}
              {(activeCategory === 'all' || activeCategory === 'unassigned') &&
                unassigned.map(item => (
                  <UnassignedClinicianCard
                    key={item.providerId}
                    item={item}
                    orgOptions={orgOptions}
                    onResolvedChange={handleResolvedChange}
                  />
                ))}
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer className="d-flex justify-content-between align-items-center">
        <span className="small text-muted">
          All actions save immediately to the directory and include an instant <strong>Undo</strong> option.
        </span>
        <Button variant="primary" onClick={handleClose}>
          Done / Close Review
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
